import { faEnvelopeOpenDollar } from '@fortawesome/pro-regular-svg-icons'
import { createSelector } from 'reselect'

import {
  getCurrentUser,
  getUserIsAdmin,
  selectMembershipTier,
} from '@/selectors/user.selectors'
import {
  selectIsPayrollActive,
  selectPayrollCompany,
  selectPayrollProfile,
  selectPayrollState,
} from '@/features/payroll/payroll.selectors'
import { PlanTier } from '@/actions/settings/billingActions'
import { selectUserPermissions } from '@/reducers/auth/permission.selectors'
import { ProductFeatureId } from '@/actions/permissionActions'
import NavigationCountIconRestructured from './NavigationCountIconRestructured'

// The tier arm exists because three Premium products omit the `payroll` attachment their
// Starter equivalents carry, so the grant alone understates what those plans include.
export const selectShouldShowPayrollSideNavLink = createSelector(
  selectPayrollState,
  selectUserPermissions,
  selectMembershipTier,
  (payrollState, userPermissions, tier) =>
    Boolean(payrollState) &&
    (userPermissions.includes(ProductFeatureId.payroll) ||
      tier === PlanTier.premium)
)
export const selectPayrollNavConfig = createSelector(
  selectShouldShowPayrollSideNavLink,
  selectIsPayrollActive,
  selectPayrollProfile,
  selectPayrollCompany,
  getCurrentUser,
  getUserIsAdmin,
  (
    shouldShowPayrollSideNavLink,
    isPayrollActive,
    payrollProfile,
    payrollCompany,
    currentUser,
    isAdmin
  ) => {
    // The gate above admits an enrolled Premium member on tier alone, and `/payroll/welcome`
    // is the upsell, not their payroll pages.
    const isEnrolledInPayroll =
      Boolean(currentUser?.payrollEnabledAt) || isPayrollActive

    if (shouldShowPayrollSideNavLink && !isEnrolledInPayroll) {
      return {
        payroll: {
          icon: faEnvelopeOpenDollar,
          label: 'Payroll',
          to: '/payroll/welcome',
          end: true,
        },
      }
    }

    // Deliberately stricter than isEnrolledInPayroll above: `getIsPayrollEnabled` gates
    // whether PAYROLL_ROUTES mount at all, on payrollEnabledAt alone, so widening this
    // offers a link to a route that is not registered.
    const showPayrollLink = Boolean(currentUser?.payrollEnabledAt) && !isAdmin

    if (!showPayrollLink) {
      return {} as Record<string, never>
    }

    if (
      !payrollProfile?.isActive ||
      payrollCompany?.company_status !== 'Approved'
    ) {
      return {
        payroll: {
          icon: faEnvelopeOpenDollar,
          label: 'Payroll',
          to: payrollProfile?.needsMigration
            ? '/payroll/migrate'
            : '/payroll/enroll/intro',
          ornament: <NavigationCountIconRestructured count={1} />,
        },
      }
    }

    return {
      payroll: {
        icon: faEnvelopeOpenDollar,
        label: 'Payroll',
        pages: [
          { end: true, label: 'Run Payroll', to: '/payroll/run_payroll' },
          {
            end: true,
            label: 'Employees & Contractors',
            to: '/payroll/employees',
          },
          { end: true, label: 'Payroll History', to: '/payroll/history' },
          {
            end: true,
            label: 'Payroll Documents',
            to: '/payroll/documents',
          },
          {
            end: true,
            label: 'Payroll Settings',
            to: '/payroll/settings',
          },
        ],
        topRoute: '/payroll',
      },
    }
  }
)
